import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/lib/router-compat";
import ResponsiveImage from "@/components/ResponsiveImage";
import { IMG } from "@/lib/optimized-images";

export default function Hero() {
  const [loadVideo, setLoadVideo] = useState(false);

  useEffect(() => {
    // Only load background video when user is on a fast non-constrained connection
    const nav = navigator as any;
    if (nav.connection?.saveData) return;

    // Defer loading video until after window load so it never competes with
    // the LCP element or the critical render path.
    const start = () => {
      const timer = setTimeout(() => {
        setLoadVideo(true);
      }, 1500);
      return () => clearTimeout(timer);
    };
    if (document.readyState === "complete") {
      return start();
    }
    window.addEventListener("load", start, { once: true });
    return () => window.removeEventListener("load", start);
  }, []);

  return (
    <section className="relative h-[100dvh] w-full overflow-hidden bg-black flex flex-col justify-between">
      {/* Background Video — decorative, muted, autoplay. aria-hidden so
          assistive tech skips it (no captions needed for a muted decorative
          background). preload="none" + poster paints first; the <source>
          ladder serves a smaller 720p file to mobile. */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="none"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 w-full h-full object-cover scale-105"
        poster="https://vibe.filesafe.space/1790273207865208150/assets/730210ac-b99f-44c9-8c1a-4a63ed19c594.avif"
      >
        {loadVideo && (
          <>
            <source
              src="https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6a7784f988808720196088c8.mp4"
              type="video/mp4"
              media="(max-width: 768px)"
            />
            <source
              src="https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6a7784f988808720196088c8.mp4"
              type="video/mp4"
            />
          </>
        )}
      </video>

      {/* Dark blue overlay focused on the text area for contrast */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#002b5c]/85 via-[#002b5c]/50 to-transparent pointer-events-none" />

      {/* Hero Content — rendered directly as static semantic HTML without framer-motion delay/opacity:0, eliminating element render delay for LCP */}
      <div className="relative z-10 my-auto pt-24 sm:pt-28 md:pt-32 lg:pt-36 pb-12">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-12 w-full">
          <div className="max-w-3xl">
            <ResponsiveImage
              rendition={IMG.heroLogo}
              alt="A Complete One Stop Interior Decoration Solution Provider"
              fetchpriority="high"
              loading="eager"
              sizes="(max-width: 640px) 130px, 234px"
              width={234}
              height={165}
              className="w-full max-w-[130px] sm:max-w-[234px] h-auto aspect-[234/165] mb-5 sm:mb-7"
            />
            <span className="inline-block text-[#ffcc29] font-heading font-bold text-sm sm:text-base md:text-lg lg:text-xl tracking-[0.2em] sm:tracking-[0.25em] uppercase mb-3 sm:mb-4">
              Interior Design & Turnkey Contracting
            </span>

            <h1 className="text-white font-heading text-4xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.95] mb-5 sm:mb-6 uppercase tracking-tight drop-shadow-lg">
              We Turn Ideas
              <br />
              Into <span className="text-[#ffcc29]">Exceptional</span> Spaces
            </h1>

            <p className="text-white/95 text-base sm:text-lg md:text-xl font-body leading-relaxed max-w-2xl drop-shadow font-medium mb-8 sm:mb-10">
              Complete turnkey solutions for commercial interiors, high-end
              retail, F&B, and executive offices - delivered on time, every
              time, across Bahrain and the GCC.
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-4 bg-[#ffcc29] text-[#002b5c] px-7 py-3.5 sm:px-8 sm:py-4 rounded-xs font-heading font-bold text-sm sm:text-base hover:bg-white transition-all uppercase tracking-wider shadow-lg active:scale-95"
              >
                Get a Free Quote
                <div className="w-8 h-[2px] bg-[#002b5c] transition-all group-hover:w-12 hidden sm:block" />
                <ArrowRight className="w-4 h-4 text-[#002b5c]" />
              </Link>

              <div className="flex flex-col gap-1 text-white drop-shadow">
                <span className="text-base sm:text-lg md:text-xl font-body font-medium tracking-wide">
                  PRECISION • AUTHORITY • PRESTIGE
                </span>
                <span className="inline-flex items-center gap-1.5 text-[#ffcc29] font-heading font-bold uppercase tracking-wider text-sm sm:text-base md:text-lg">
                  180+ brands · ISO triple-certified
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="relative z-20 pb-6 hidden md:flex flex-col items-center gap-2 text-white/70 mx-auto">
        <span className="text-[10px] font-heading font-bold tracking-[0.3em] uppercase">
          Scroll
        </span>
        <div className="w-[1px] h-8 bg-gradient-to-b from-[#ffcc29] to-transparent animate-bounce" />
      </div>
    </section>
  );
}
